import { isCancel } from 'axios'
import { makeAutoObservable } from 'mobx'
import { RequestCancelAxios } from 'shared/api/lib'
import { ContactsAnalyticsApi } from 'entities/Analytics/api/contactsAnalytics'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ContactsAnalyticsKeys } from 'entities/Analytics/api/types'
import { RowContactsComparison } from 'entities/Analytics/model/Table/RowContactsComparison'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { ExportAnalyticsModalStore } from 'widgets/ExportAnalyticsModal'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import { ContactsChartDataStore } from 'pages/analytics/pages/contacts/ui/Chart'
import {
  AnalyticsComparisonTableStore,
  type IAnalyticsComparisonTableConfig,
} from 'pages/analytics/store/analyticsComparisonTableStore'

export type ContactsComparisonTableStore = AnalyticsComparisonTableStore<
  ContactsAnalyticsKeys,
  RowContactsComparison
>

export type IContactsComparisonTableConfig = IAnalyticsComparisonTableConfig<
  ContactsAnalyticsKeys,
  RowContactsComparison
>

export class ContactsAnalyticsPageStore {
  private readonly _chartDataStore: ContactsChartDataStore
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _comparisonTableStore: ContactsComparisonTableStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _error: string | null = null
  private _pageLoading = false
  private _chartLoading = false

  constructor(entities: IEntitiesStore) {
    this._chartDataStore = new ContactsChartDataStore()
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: (loadingType) => this._fetchAnalytics(loadingType),
      additionalFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.tags,
      ],
      breakdownFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.tags,
      ],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore(
      ContactsAnalyticsApi.exportAnalytics
    )

    const comparisonTableConfig: IContactsComparisonTableConfig = {
      tableKey: 'contactsComparisonTable',
      getAnalytics: ContactsAnalyticsApi.getAnalytics,
      createRow: (data) => new RowContactsComparison(data),
      createMetricsRecord: () => ({
        new_contacts: 0,
        opted_out: 0,
      }),
    }

    this._comparisonTableStore = new AnalyticsComparisonTableStore(
      comparisonTableConfig,
      () => this._analyticsFiltersStore.analyticsFiltersParams,
      () => this._analyticsFiltersStore.breakdownFilterStore.appliedBreakdownFilterType,
      entities
    )
    makeAutoObservable(this)
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get breakdownFilterStore() {
    return this._analyticsFiltersStore.breakdownFilterStore
  }

  get chartFilterStore() {
    return this._analyticsFiltersStore.chartFilterStore
  }

  get chartDataStore() {
    return this._chartDataStore
  }

  get comparisonTableStore() {
    return this._comparisonTableStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get chartLoading() {
    return this._chartLoading
  }

  get error() {
    return this._error
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._comparisonTableStore.init()
    this._fetchAnalytics('page')
  }

  private _fetchAnalytics = async (loadingType: 'page' | 'chart' = 'page') => {
    if (loadingType === 'page') {
      this._pageLoading = true
    } else {
      this._chartLoading = true
    }

    try {
      this._error = null
      const { analyticsFiltersParams } = this._analyticsFiltersStore
      const { period } = this._analyticsFiltersStore.chartFilterStore

      this._requestCancelAxios.init()

      await ContactsAnalyticsApi.preloadAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      const { data } = await ContactsAnalyticsApi.getAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      this._chartDataStore.setRawData(data, period)
    } catch (error) {
      if (!isCancel(error)) {
        this._error = 'Failed to load analytics data'
      }
    } finally {
      if (loadingType === 'page') {
        this._pageLoading = false
      } else {
        this._chartLoading = false
      }
    }
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  dispose = () => {
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
    this._chartDataStore.dispose?.()
    this._comparisonTableStore.dispose()
    this._exportAnalyticsModalStore.dispose()
  }
}
